import React, { useMemo } from "react";
import { useField, useFormikContext } from "formik";
import { TransferRuleDirection } from "@common/types/transactionRule";
import { useEntities } from "@hooks/useEntities";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { Menu } from "@design-system/Menu/Menu.lib";
import { Picker } from "@design-system/Picker/Picker.lib";
import styles from "../TransferRuleForm.module.scss";
import {
    FormRuleActionType,
    FormRuleConditionType,
    TransactionRuleActionFormSchema,
} from "../../TransactionRuleForm/constants";
import { TransferRuleFormActions, TransferRuleFormSchema } from "../constants";
import { CategoryActionForm } from "./CategoryActionForm";
import { AccountActionForm } from "./AccountActionForm";

interface Props {
    transferDirection: TransferRuleDirection;
    index: number;
    onRemove: (index: number) => void;
}

const actionTypeLabels: Record<string, string> = {
    [FormRuleActionType.CATEGORY]: "Set category as",
    [FormRuleActionType.ACCOUNT_ID]: "Assign account",
    [FormRuleActionType.ACCOUNT_NAME]: "Assign account",
};

export const TransferRuleActionForm: React.FC<Props> = ({
    transferDirection,
    index,
    onRemove,
}) => {
    const { values } = useFormikContext<TransferRuleFormSchema>();
    const entities = useEntities();
    const { enabled: isGlFirst } = useIsGlFirst();

    const [{ value: allActions }] = useField<TransactionRuleActionFormSchema[]>(
        `actions.${transferDirection}`,
    );
    const [actionTypeField, , actionTypeHelpers] = useField<
        FormRuleActionType | undefined
    >(`actions.${transferDirection}.${index}.actionType`);

    const actionType = actionTypeField.value;
    const canRemove = allActions.length > 1;

    const isPersonalEntity = useMemo(() => {
        const entityCondition = values.conditions[transferDirection].find(
            (condition) =>
                condition.conditionType === FormRuleConditionType.ENTITY,
        );
        const entityIds = entityCondition?.entityValue ?? [];
        return entityIds.some(
            (id) => entities.find((e) => e.id === id)?.isPersonal,
        );
    }, [values.conditions, transferDirection, entities]);

    const selectedTypes: FormRuleActionType[] = useMemo(
        () =>
            allActions.reduce<FormRuleActionType[]>((acc, a) => {
                if (a.actionType === undefined) {
                    return acc;
                }
                acc.push(
                    a.actionType === FormRuleActionType.ACCOUNT_NAME
                        ? FormRuleActionType.ACCOUNT_ID
                        : a.actionType,
                );
                return acc;
            }, []),
        [allActions],
    );

    const actionTypesOptions = useMemo(
        () =>
            TransferRuleFormActions.filter(
                (v) =>
                    v !== FormRuleActionType.ACCOUNT_ID ||
                    !isPersonalEntity ||
                    actionType === FormRuleActionType.ACCOUNT_ID ||
                    actionType === FormRuleActionType.ACCOUNT_NAME,
            )
                .filter(
                    // GL-first workspaces classify by general ledger, so the
                    // category action is never selectable as a new option.
                    // Keep it when it's already the action's selected type so
                    // pre-existing rules remain visible and editable.
                    (v) =>
                        !isGlFirst ||
                        v !== FormRuleActionType.CATEGORY ||
                        v === actionType,
                )
                .filter((v) => !selectedTypes.includes(v) || v === actionType)
                .map((value) => ({
                    value,
                    label: actionTypeLabels[value] ?? value,
                })),
        [actionType, selectedTypes, isPersonalEntity, isGlFirst],
    );

    return (
        <>
            <div className={styles.sectionRowContent}>
                <Picker.Select
                    items={actionTypesOptions}
                    value={actionType ?? null}
                    onValueChange={(value) => {
                        if (value !== null) {
                            void actionTypeHelpers.setValue(value);
                        }
                    }}
                    renderValue={([value]) => actionTypeLabels[value] ?? value}
                    placeholder="Select action type"
                />

                {actionType === FormRuleActionType.CATEGORY && (
                    <CategoryActionForm
                        transferDirection={transferDirection}
                        index={index}
                    />
                )}

                {(actionType === FormRuleActionType.ACCOUNT_ID ||
                    actionType === FormRuleActionType.ACCOUNT_NAME) && (
                    <AccountActionForm
                        transferDirection={transferDirection}
                        index={index}
                    />
                )}
            </div>

            {canRemove && (
                <Menu.More>
                    <Menu.Item onClick={() => onRemove(index)} destructive>
                        Remove
                    </Menu.Item>
                </Menu.More>
            )}
        </>
    );
};
